'use client';

import React, { useState, useCallback } from 'react';
import { History } from 'lucide-react';
import { clsx } from 'clsx';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useCart } from './cart-provider';
import { ConfirmDialog } from './confirm-dialog';
import { CurrentRequestView } from './current-request-view';
import { RequestHistoryList } from './request-history-list';
import { useReorderRequest } from './use-reorder-request';
import { ReorderChoiceDialog } from './reorder-choice-dialog';

type AnfrageView = 'current' | 'verlauf';

export function MaterialanfragePage() {
  const { itemCount, clear } = useCart();
  const { contextHeading } = useAdmin();

  const [view, setView] = useState<AnfrageView>('current');
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);

  const reorder = useReorderRequest();

  const handleReorder = useCallback(
    (request: Parameters<typeof reorder.requestReorder>[0]) => {
      reorder.requestReorder(request);
      if (itemCount === 0) setView('current'); // direkte Übernahme bei leerem Entwurf
    },
    [reorder, itemCount],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary tracking-tight">
            {contextHeading('Anfrage')}
          </h1>
          <p className="text-text-muted mt-1">
            {view === 'verlauf'
              ? 'Ihre letzten Anfragen (max. 10)'
              : `${itemCount} ${itemCount === 1 ? 'Position' : 'Positionen'}`}
          </p>
        </div>
        {view === 'current' && itemCount > 0 && (
          <button
            onClick={() => setIsClearDialogOpen(true)}
            className="px-4 py-2 text-sm font-medium text-zinc-600 border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors self-start"
          >
            Leeren
          </button>
        )}
      </div>

      {/* Reiter-Toggle */}
      <div className="flex rounded-xl border border-zinc-200 bg-white p-1 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
        <button
          type="button"
          onClick={() => setView('current')}
          className={clsx(
            'flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-colors',
            view === 'current' ? 'bg-zinc-100 text-secondary' : 'text-zinc-500 hover:text-secondary',
          )}
        >
          Aktuelle Anfrage
          {itemCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary text-white text-xs font-bold">
              {itemCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setView('verlauf')}
          className={clsx(
            'flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-colors',
            view === 'verlauf' ? 'bg-zinc-100 text-secondary' : 'text-zinc-500 hover:text-secondary',
          )}
        >
          <History size={15} />
          Verlauf
        </button>
      </div>

      {/* Body */}
      {view === 'verlauf' ? (
        <RequestHistoryList onReorder={handleReorder} />
      ) : (
        <CurrentRequestView />
      )}

      {/* Dialoge */}
      <ReorderChoiceDialog reorder={reorder} onConfirmed={() => setView('current')} />
      <ConfirmDialog
        isOpen={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onConfirm={clear}
        title="Anfrage leeren?"
        message={`Anfrage wirklich leeren? ${itemCount} ${itemCount === 1 ? 'Position wird' : 'Positionen werden'} entfernt.`}
        confirmLabel="Leeren"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
}
